import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Popup } from 'semantic-ui-react';

import { numberToTwoDecimals, formatCurrency } from '../../utils/valueFormatter';

const useLegendKeys = ({ legendKeys }) => {
  const [visibility, setVisibility] = useState({});
  const MAX_MODELS_VISIBLE = 4;

  useEffect(() => {
    const visible = legendKeys.reduce((accu, curr, index) => ({
      ...accu,
      [curr]: index < MAX_MODELS_VISIBLE,
    }), {});
    setVisibility(visible);
  }, [legendKeys]);

  const updateVisibility = evt => {
    const { dataKey: clickedKey } = evt;
    const isVisible = !visibility[clickedKey];
    if (isVisible) {
      const currentVisibleCount = Object.values(visibility).filter(v => v).length;
      if (currentVisibleCount >= MAX_MODELS_VISIBLE) {
        return;
      }
    }
    setVisibility({ ...visibility, [clickedKey]: isVisible });
  };

  const visibleKeys = useMemo(() => Object.keys(visibility).filter(k => visibility[k]), [visibility]);

  return { visibleKeys, updateVisibility };
};

const LegendContent = ({ payload, onClick, execDtNameMap }) => (
  <div style={{ textAlign: 'center' }}>
    {payload.map(entry => (
      <Popup
        key={entry.value}
        trigger={(
          <button
            type="button"
            style={{
              color: entry.color,
              margin: '0 10px',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
            }}
            onClick={() => onClick({ dataKey: entry.value })}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                onClick({ dataKey: entry.value });
              }
            }}
          >
            {entry.value}
          </button>
        )}
        content={`Execution Date: ${execDtNameMap[entry.value]}`}
        position="top center"
        size="small"
      />
    ))}
  </div>
);

const ModelBarChart = ({
  data, dataKey,
  colorMap, height,
  kpiType,
  execDtNameMap,
}) => {
  const legendKeys = useMemo(() => (
    data.length ? Object.keys(data[0]).filter(k => k !== dataKey) : []
  ), [data, dataKey]);
  const { visibleKeys, updateVisibility } = useLegendKeys({ legendKeys });

  const chartHeight = useMemo(() => {
    if (!data.length) return 0;
    const rowHeight = 50;
    const padding = 100;
    const visibleBarsCount = visibleKeys.length;
    const dataLength = data.length;
    const calculatedHeight = (dataLength * rowHeight * Math.max(1, visibleBarsCount * 0.5)) + padding;
    return height || Math.min(calculatedHeight, 1000);
  }, [data.length, visibleKeys.length, height]);

  const legendContentRenderer = useCallback(({ payload }) => (
    <LegendContent
      payload={payload}
      onClick={updateVisibility}
      execDtNameMap={execDtNameMap}
    />
  ), [updateVisibility, execDtNameMap]);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} barCategoryGap="20%" layout="vertical">
        <XAxis
          type="number"
          tickLine={false}
        />
        <YAxis
          dataKey={dataKey}
          tickLine={false}
          type="category"
          width={250}
        />
        {legendKeys
          .map(k => (
            <Bar
              key={k}
              dataKey={k}
              fill={colorMap[k]}
              hide={!visibleKeys.includes(k)}
              barSize={8}
            />
          ))}
        <Tooltip
          cursor={false}
          formatter={value => (kpiType === 'conversions' ? formatCurrency(value) : numberToTwoDecimals(value))}
        />
        <Legend
          verticalAlign="top"
          height={36}
          onClick={updateVisibility}
          content={legendContentRenderer}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ModelBarChart;
